/*
 * F4F Product Stock Status Widget
 *
 * Fetches per-region stock availability from the F4F API and displays
 * a color-coded badge below the buy button on product pages.
 *
 * Depends on: f4f-api-client.js (window.F4FApi.getProductStock, getWarehouses)
 *
 * Country detection reads the Shopify localization form hidden input,
 * then maps the ISO country code to the serving warehouse region via
 * the GET /api/v1/warehouses endpoint (cached per session).
 */

(function () {
  'use strict';

  // ── Warehouse mapping (fetched from API, cached) ───────
  // Built from GET /api/v1/warehouses response: { warehouses: { US: ["US","AG",...], ... } }
  // Inverted to country→warehouse lookup. CN is catch-all for unmapped countries.
  var countryToWarehouse = null; // Populated on first use
  var warehousePromise = null;   // Singleton promise to avoid duplicate fetches
  var DEFAULT_WAREHOUSE = 'CN';

  function loadWarehouseMapping() {
    if (warehousePromise) return warehousePromise;

    if (!window.F4FApi || !window.F4FApi.getWarehouses) {
      return Promise.resolve(null);
    }

    warehousePromise = window.F4FApi.getWarehouses()
      .then(function (data) {
        if (!data || !data.warehouses) return null;
        // Invert the mapping: { US: ["US","AG"] } → { US: "US", AG: "US" }
        countryToWarehouse = {};
        Object.keys(data.warehouses).forEach(function (warehouseCode) {
          var countries = data.warehouses[warehouseCode];
          countries.forEach(function (countryCode) {
            countryToWarehouse[countryCode] = warehouseCode;
          });
        });
        return countryToWarehouse;
      })
      .catch(function (err) {
        console.warn('[F4F Stock] Failed to load warehouse mapping:', err);
        return null;
      });

    return warehousePromise;
  }

  // ── Status display config ──────────────────────────────
  var STATUS_LABELS = {
    in_stock: 'In Stock',
    low_stock: 'Limited Stock',
    out_of_stock: 'Out of Stock',
    pre_order: 'Pre-Order',
    waitlist: 'Waitlist'
  };

  var WAREHOUSE_LABELS = {
    US: 'United States',
    UK: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    SK: 'Europe',
    CN: 'Asia'
  };

  // ── DOM helpers ────────────────────────────────────────

  function getCustomerCountry() {
    var input = document.querySelector('input[name="country_code"]');
    if (input && input.value) return input.value.toUpperCase();
    if (window.mdApp_currentCountryCode) return window.mdApp_currentCountryCode.toUpperCase();
    return '';
  }

  function getWarehouseRegion(countryCode) {
    if (!countryToWarehouse) return DEFAULT_WAREHOUSE;
    return countryToWarehouse[countryCode] || DEFAULT_WAREHOUSE;
  }

  function getCurrentSku() {
    // Try the payment options data attribute first (always has first variant SKU)
    var paymentEl = document.querySelector('[data-product-payment-selection-sku]');
    if (paymentEl) return paymentEl.getAttribute('data-product-payment-selection-sku');
    // Fallback to the SKU display element
    var skuEl = document.querySelector('[data-js-product-sku]');
    if (skuEl) return skuEl.textContent.replace(/^SKU:\s*/i, '').trim();
    return '';
  }

  // ── Widget rendering ───────────────────────────────────

  var containerEl = null;
  var currentSku = '';
  var stockCache = {};

  function createContainer() {
    containerEl = document.createElement('div');
    containerEl.className = 'f4f-stock';

    // Insert after the product title (h1.product-title), in normal document flow.
    // The .product-badges-holder is position:absolute so we can't insert near it.
    var productTitle = document.querySelector('product-header .product-title');
    if (productTitle) {
      productTitle.parentNode.insertBefore(containerEl, productTitle.nextSibling);
      return;
    }
    // Fallback: insert before the price area
    var priceReviews = document.querySelector('.price-reviews');
    if (priceReviews) {
      priceReviews.parentNode.insertBefore(containerEl, priceReviews);
      return;
    }
    // Last resort: insert after the add-to-cart form
    var productForm = document.querySelector('product-form');
    if (productForm) {
      productForm.parentNode.insertBefore(containerEl, productForm.nextSibling);
    }
  }

  function hideExistingBadge() {
    // Hide the theme's static stock status badge (e.g. "PRE-ORDER", "In Stock")
    // since our dynamic API-driven badge replaces it with per-region data
    var statusBadges = document.querySelectorAll('.product-badge__status');
    statusBadges.forEach(function (badge) {
      badge.style.display = 'none';
    });
  }

  function renderLoading() {
    if (!containerEl) return;
    containerEl.innerHTML =
      '<span class="f4f-stock__badge f4f-stock__badge--loading">' +
        '<span class="f4f-stock__dot"></span>' +
        'Loading...' +
      '</span>';
    containerEl.classList.remove('f4f-stock--hidden');
  }

  function renderBadge(status, warehouseCode) {
    if (!containerEl) return;
    var label = STATUS_LABELS[status] || status;
    var regionLabel = WAREHOUSE_LABELS[warehouseCode] || warehouseCode;

    containerEl.innerHTML =
      '<span class="f4f-stock__badge f4f-stock__badge--' + status + '">' +
        '<span class="f4f-stock__dot"></span>' +
        label +
        '<span class="f4f-stock__region">(' + regionLabel + ')</span>' +
      '</span>';
    containerEl.classList.remove('f4f-stock--hidden');
    containerEl.classList.add('f4f-stock--loaded');
    hideExistingBadge();
  }

  function hide() {
    if (containerEl) containerEl.classList.add('f4f-stock--hidden');
  }

  // ── Fetch and display ──────────────────────────────────

  function fetchAndRender(sku) {
    if (!sku) { hide(); return; }
    if (sku === currentSku) return;
    currentSku = sku;

    // Check cache first
    if (stockCache[sku]) {
      displayStock(stockCache[sku]);
      return;
    }

    renderLoading();

    if (!window.F4FApi || !window.F4FApi.getProductStock) {
      console.warn('[F4F Stock] F4FApi.getProductStock not available');
      hide();
      return;
    }

    window.F4FApi.getProductStock(sku)
      .then(function (data) {
        if (!data) { hide(); return; }
        stockCache[sku] = data;
        // Only render if this is still the current SKU (prevents race conditions)
        if (currentSku === sku) displayStock(data);
      })
      .catch(function (err) {
        console.warn('[F4F Stock] Failed to fetch stock for ' + sku + ':', err);
        hide();
      });
  }

  function displayStock(data) {
    var overallStatus = data.status;
    var country = getCustomerCountry();
    var warehouse = getWarehouseRegion(country);

    // For non-in_stock statuses, the status is uniform across all regions
    if (overallStatus !== 'in_stock') {
      renderBadge(overallStatus, warehouse);
      return;
    }

    // For in_stock products, show the customer's specific region availability
    var regionStatus = (data.regions && data.regions[warehouse]) || 'out_of_stock';
    renderBadge(regionStatus, warehouse);
  }

  // ── Variant change listener ────────────────────────────
  // The theme dispatches a custom 'variant-change' event on <product-page>
  // and also updates [data-js-product-sku] on variant selection.

  function onVariantChange() {
    // Small delay to let the theme update the SKU display element
    setTimeout(function () {
      var sku = getCurrentSku();
      if (sku && sku !== currentSku) {
        currentSku = ''; // Reset to force re-fetch
        fetchAndRender(sku);
      }
    }, 50);
  }

  // ── Init ───────────────────────────────────────────────

  function init() {
    // Skip coming-soon products: the storefront intentionally hides
    // pricing and stock/status info until pre-orders open, so the API
    // badge (e.g. "Pre-Order (United States)") should stay hidden too.
    // main-product.liquid sets the .product-page-coming-soon class when
    // product.metafields.f4f.available_from is in the future.
    var productPage = document.querySelector('product-page');
    if (productPage && productPage.classList.contains('product-page-coming-soon')) {
      console.log('[F4F Stock] Coming-soon product, skipping stock badge.');
      return;
    }

    console.log('[F4F Stock] Initializing...');
    createContainer();
    if (!containerEl) {
      console.warn('[F4F Stock] Could not create container — no insertion point found');
      return;
    }
    console.log('[F4F Stock] Container created:', containerEl.parentElement?.tagName, containerEl.parentElement?.className);

    // Load warehouse mapping first, then fetch stock for the current SKU.
    // Both requests fire in parallel for speed.
    var sku = getCurrentSku();
    console.log('[F4F Stock] SKU:', sku);
    console.log('[F4F Stock] F4FApi available:', !!window.F4FApi, 'getProductStock:', !!window.F4FApi?.getProductStock);
    var mappingReady = loadWarehouseMapping();
    var stockReady = null;

    if (sku && window.F4FApi && window.F4FApi.getProductStock) {
      renderLoading();
      currentSku = sku;
      stockReady = window.F4FApi.getProductStock(sku)
        .then(function (data) {
          console.log('[F4F Stock] API response for ' + sku + ':', data);
          if (data) stockCache[sku] = data;
          return data;
        })
        .catch(function (err) {
          console.warn('[F4F Stock] Failed to fetch stock for ' + sku + ':', err);
          return null;
        });
    }

    // Once both are done, render
    Promise.all([mappingReady, stockReady || Promise.resolve(null)])
      .then(function (results) {
        var data = results[1];
        if (!data || currentSku !== sku) { hide(); return; }
        displayStock(data);
      });

    // Listen for variant changes
    var productPage = document.querySelector('product-page');
    if (productPage) {
      productPage.addEventListener('variant-change', onVariantChange);
    }

    // Also observe SKU text changes as a fallback
    var skuEl = document.querySelector('[data-js-product-sku]');
    if (skuEl && window.MutationObserver) {
      var observer = new MutationObserver(onVariantChange);
      observer.observe(skuEl, { childList: true, characterData: true, subtree: true });
    }
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
